import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PasswordField from '../components/PasswordField';

export default function EditProfile() {
  const { user, loading: authLoading } = useAuth();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    country: '',
    profile_picture_url: '',
    postal_code: '',
    block_no: '',
    building: '',
    road_name: '',
    address_line: '',
    unit_no: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [wellisIdForm, setWellisIdForm] = useState({
    wellisId: '',
  });
  const [currentWellisId, setCurrentWellisId] = useState('');
  const [wellisIdError, setWellisIdError] = useState('');
  const [wellisIdLoading, setWellisIdLoading] = useState(false);
  const [wellisIdAvailable, setWellisIdAvailable] = useState(null);
  const [checkingWellisId, setCheckingWellisId] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }

    // Fetch full user profile from backend
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/profile-data`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          console.error('Failed to fetch profile:', response.status);
          return;
        }

        const profileData = await response.json();
        setFormData({
          first_name: profileData.first_name || '',
          last_name: profileData.last_name || '',
          phone: profileData.phone || '',
          country: profileData.country || '',
          profile_picture_url: profileData.profile_picture_url || '',
          postal_code: profileData.postal_code || '',
          block_no: profileData.block_no || '',
          building: profileData.building || '',
          road_name: profileData.road_name || '',
          address_line: profileData.address_line || '',
          unit_no: profileData.unit_no || '',
        });
        // Update Wellis ID display if available
        if (profileData.affiliate_code) {
          setCurrentWellisId(profileData.affiliate_code);
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
      }
    };

    fetchProfile();
  }, [authLoading, user, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update profile');
      }

      setSuccess('Profile updated successfully');
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value,
    }));
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validatePassword = () => {
    const errors = {};

    if (!passwordForm.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordForm.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    }

    if (!passwordForm.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (user?.auth_method === 'email' && !passwordForm.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!validatePassword()) return;

    setPasswordLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const payload = {
        newPassword: passwordForm.newPassword,
      };

      // Only include current password if user has email auth
      if (user?.auth_method === 'email') {
        payload.currentPassword = passwordForm.currentPassword;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to change password');
      }

      setSuccess('Password changed successfully');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      setPasswordErrors({ submit: err.message });
    } finally {
      setPasswordLoading(false);
    }
  };

  const checkWellisIdUniqueness = async () => {
    if (!wellisIdForm.wellisId || wellisIdForm.wellisId.length !== 10) {
      setWellisIdError('Wellis ID must be exactly 10 characters');
      setWellisIdAvailable(false);
      return;
    }

    setCheckingWellisId(true);
    setWellisIdError('');
    setWellisIdAvailable(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/check-wellis-id`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wellis_id: `WELLIS-${wellisIdForm.wellisId.toUpperCase()}` }),
      });

      const data = await response.json();

      if (!response.ok) {
        setWellisIdError(data.error || 'Failed to check Wellis ID availability');
        setWellisIdAvailable(false);
      } else if (data.available) {
        setWellisIdAvailable(true);
        setWellisIdError('');
      } else {
        setWellisIdAvailable(false);
        setWellisIdError('Wellis ID is already taken');
      }
    } catch (err) {
      setWellisIdError(err.message || 'Failed to check Wellis ID availability');
      setWellisIdAvailable(false);
    } finally {
      setCheckingWellisId(false);
    }
  };

  const handleWellisIdChange = (e) => {
    const value = e.target.value.toUpperCase();
    setWellisIdForm({ wellisId: value });
    setWellisIdError('');
    setWellisIdAvailable(null);
  };

  const handleWellisIdSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!wellisIdForm.wellisId.trim()) {
      setWellisIdError('Wellis ID is required');
      return;
    }

    if (wellisIdForm.wellisId.length !== 10) {
      setWellisIdError('Wellis ID must be exactly 10 characters');
      return;
    }

    if (!wellisIdAvailable) {
      setWellisIdError('Please verify the Wellis ID availability first');
      return;
    }

    setWellisIdLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/change-wellis-id`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ wellis_id: `WELLIS-${wellisIdForm.wellisId.toUpperCase()}` }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to change Wellis ID');
      }

      // Update the current Wellis ID display with the new one
      const newWellisId = `WELLIS-${wellisIdForm.wellisId.toUpperCase()}`;
      setCurrentWellisId(newWellisId);

      setSuccess('Wellis ID changed successfully');
      setWellisIdForm({ wellisId: '' });
      setWellisIdAvailable(null);
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      setWellisIdError(err.message || 'Failed to change Wellis ID');
    } finally {
      setWellisIdLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-margin-mobile pt-24 pb-32">
      <div className="max-w-md mx-auto">
        <div className="mb-8">
          <h1 className="text-headline-lg text-on-background mb-2">Settings</h1>
          <p className="text-body-md text-on-background/60">Manage your account</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-outline overflow-x-auto">
          <button
            onClick={() => setActiveTab('profile')}
            className={`pb-3 font-medium text-body-sm transition-colors whitespace-nowrap ${
              activeTab === 'profile'
                ? 'text-primary border-b-2 border-primary'
                : 'text-on-background/60 hover:text-on-background'
            }`}
          >
            Profile
          </button>
          <button
            onClick={() => setActiveTab('wellis-id')}
            className={`pb-3 font-medium text-body-sm transition-colors whitespace-nowrap ${
              activeTab === 'wellis-id'
                ? 'text-primary border-b-2 border-primary'
                : 'text-on-background/60 hover:text-on-background'
            }`}
          >
            Wellis ID
          </button>
          <button
            onClick={() => setActiveTab('address')}
            className={`pb-3 font-medium text-body-sm transition-colors whitespace-nowrap ${
              activeTab === 'address'
                ? 'text-primary border-b-2 border-primary'
                : 'text-on-background/60 hover:text-on-background'
            }`}
          >
            Address
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`pb-3 font-medium text-body-sm transition-colors whitespace-nowrap ${
              activeTab === 'password'
                ? 'text-primary border-b-2 border-primary'
                : 'text-on-background/60 hover:text-on-background'
            }`}
          >
            Password
          </button>
        </div>

        {error && (
          <div className="bg-error/10 border border-error text-error p-4 rounded-lg mb-6 text-body-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-success/10 border border-success text-success p-4 rounded-lg mb-6 text-body-sm">
            {success}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-body-sm font-medium text-on-background mb-2">
                First Name
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-surface border border-outline rounded-lg text-on-background text-body-sm focus:outline-none focus:border-primary"
                placeholder="First name"
              />
            </div>

            <div>
              <label className="block text-body-sm font-medium text-on-background mb-2">
                Last Name
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-surface border border-outline rounded-lg text-on-background text-body-sm focus:outline-none focus:border-primary"
                placeholder="Last name"
              />
            </div>

            <div>
              <label className="block text-body-sm font-medium text-on-background mb-2">
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-surface border border-outline rounded-lg text-on-background text-body-sm focus:outline-none focus:border-primary"
                placeholder="+1 (555) 000-0000"
              />
            </div>

            <div>
              <label className="block text-body-sm font-medium text-on-background mb-2">
                Country
              </label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-surface border border-outline rounded-lg text-on-background text-body-sm focus:outline-none focus:border-primary"
                placeholder="Country"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-2 bg-primary text-on-primary rounded-lg hover:opacity-90 transition-opacity font-medium disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        )}

        {/* Wellis ID Tab */}
        {activeTab === 'wellis-id' && (
          <form onSubmit={handleWellisIdSubmit} className="space-y-6">
            <div>
              <label className="block text-body-sm font-medium text-on-background mb-2">
                Your Current Wellis ID
              </label>
              <div className="px-4 py-3 bg-surface border border-outline rounded-lg">
                <p className="text-body-sm text-on-background font-mono">
                  {currentWellisId || 'Not set'}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-body-sm font-medium text-on-background mb-2">
                New Wellis ID (10 characters)
              </label>
              <div className="flex items-center gap-0 bg-surface border border-outline rounded-lg overflow-hidden">
                <span className="px-4 py-2 font-mono font-medium" style={{ color: '#c9a961', backgroundColor: '#0d2818' }}>
                  WELLIS-
                </span>
                <input
                  type="text"
                  value={wellisIdForm.wellisId}
                  onChange={handleWellisIdChange}
                  maxLength="10"
                  placeholder="ABCDEFGH12"
                  className="flex-1 px-3 py-2 bg-surface text-on-background text-body-sm focus:outline-none uppercase"
                  style={{ border: 'none' }}
                />
              </div>
              <p className="text-body-xs text-on-background/60 mt-2">
                Enter 10 characters (letters and numbers)
              </p>
            </div>

            {wellisIdError && (
              <div className="bg-error/10 border border-error text-error p-4 rounded-lg text-body-sm">
                {wellisIdError}
              </div>
            )}

            {wellisIdAvailable === true && (
              <div className="bg-success/10 border border-success text-success p-4 rounded-lg text-body-sm">
                ✓ Wellis ID is available
              </div>
            )}

            <button
              type="button"
              onClick={checkWellisIdUniqueness}
              disabled={!wellisIdForm.wellisId || wellisIdForm.wellisId.length !== 10 || checkingWellisId}
              className="w-full px-4 py-2 bg-on-background/10 text-on-background rounded-lg hover:opacity-90 transition-opacity font-medium disabled:opacity-50 border border-outline"
            >
              {checkingWellisId ? 'Checking...' : 'Check Availability'}
            </button>

            <button
              type="submit"
              disabled={wellisIdLoading || !wellisIdAvailable || !wellisIdForm.wellisId}
              className="w-full px-4 py-2 bg-primary text-on-primary rounded-lg hover:opacity-90 transition-opacity font-medium disabled:opacity-50"
            >
              {wellisIdLoading ? 'Changing...' : 'Change Wellis ID'}
            </button>
          </form>
        )}

        {/* Address Tab */}
        {activeTab === 'address' && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-body-sm font-medium text-on-background mb-2">
                Postal Code
              </label>
              <input
                type="text"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-surface border border-outline rounded-lg text-on-background text-body-sm focus:outline-none focus:border-primary"
                placeholder="e.g., 238873"
              />
            </div>

            <div>
              <label className="block text-body-sm font-medium text-on-background mb-2">
                Block No.
              </label>
              <input
                type="text"
                name="block_no"
                value={formData.block_no}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-surface border border-outline rounded-lg text-on-background text-body-sm focus:outline-none focus:border-primary"
                placeholder="e.g., 123"
              />
            </div>

            <div>
              <label className="block text-body-sm font-medium text-on-background mb-2">
                Building
              </label>
              <input
                type="text"
                name="building"
                value={formData.building}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-surface border border-outline rounded-lg text-on-background text-body-sm focus:outline-none focus:border-primary"
                placeholder="e.g., Building A"
              />
            </div>

            <div>
              <label className="block text-body-sm font-medium text-on-background mb-2">
                Road Name
              </label>
              <input
                type="text"
                name="road_name"
                value={formData.road_name}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-surface border border-outline rounded-lg text-on-background text-body-sm focus:outline-none focus:border-primary"
                placeholder="e.g., Orchard Road"
              />
            </div>

            <div>
              <label className="block text-body-sm font-medium text-on-background mb-2">
                Address Line
              </label>
              <input
                type="text"
                name="address_line"
                value={formData.address_line}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-surface border border-outline rounded-lg text-on-background text-body-sm focus:outline-none focus:border-primary"
                placeholder="e.g., 123 Orchard Road"
              />
            </div>

            <div>
              <label className="block text-body-sm font-medium text-on-background mb-2">
                Unit No.
              </label>
              <input
                type="text"
                name="unit_no"
                value={formData.unit_no}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-surface border border-outline rounded-lg text-on-background text-body-sm focus:outline-none focus:border-primary"
                placeholder="e.g., #05-01"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-2 bg-primary text-on-primary rounded-lg hover:opacity-90 transition-opacity font-medium disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Address'}
            </button>
          </form>
        )}

        {/* Password Tab */}
        {activeTab === 'password' && (
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            {user?.auth_method === 'google' && (
              <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mb-6">
                <p className="text-body-sm text-on-background">
                  <strong>💡 Wellis Password</strong>
                </p>
                <p className="text-body-xs text-on-background/80 mt-2">
                  This password is separate from your Google account and provides additional security for your Wellis account.
                </p>
              </div>
            )}

            {passwordErrors.submit && (
              <div className="bg-error/10 border border-error text-error p-4 rounded-lg text-body-sm">
                {passwordErrors.submit}
              </div>
            )}

            {user?.auth_method === 'email' && (
              <PasswordField
                label="Current Password"
                name="currentPassword"
                value={passwordForm.currentPassword}
                onChange={handlePasswordChange}
                placeholder="Enter your current password"
                error={passwordErrors.currentPassword}
                autoComplete="current-password"
                required
              />
            )}

            <PasswordField
              label="New Password"
              name="newPassword"
              value={passwordForm.newPassword}
              onChange={handlePasswordChange}
              placeholder="Create a strong password (min 8 characters)"
              error={passwordErrors.newPassword}
              autoComplete="new-password"
              required
            />

            <PasswordField
              label="Confirm Password"
              name="confirmPassword"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange}
              placeholder="Re-enter your password"
              error={passwordErrors.confirmPassword}
              autoComplete="new-password"
              required
            />

            <div className="bg-on-background/5 rounded-lg p-3">
              <p className="text-body-xs font-medium text-on-background mb-2">
                Password Requirements:
              </p>
              <ul className="text-body-xs text-on-background/70 space-y-1 ml-4 list-disc">
                <li>At least 8 characters</li>
                <li>Mix of uppercase and lowercase (recommended)</li>
                <li>Include numbers and symbols (recommended)</li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={passwordLoading}
              className="w-full px-4 py-2 bg-primary text-on-primary rounded-lg hover:opacity-90 transition-opacity font-medium disabled:opacity-50"
            >
              {passwordLoading ? 'Changing Password...' : 'Change Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
